<template>
    <div class="sort-code-input">
        <input ref="part1"
               data-testid="sort-code-part-1-input"
               type="text"
               inputmode="numeric"
               class="form-control input-lg sort-code-input__part"
               placeholder="00"
               autocomplete="no-auto-fill"
               :value="parts[0]"
               @input="onInput(0, $event)"
               @keydown="onKeydown(0, $event)"
               @paste="onPaste($event)"
               @focus="$event.target.select()" />
        <span class="sort-code-input__separator">-</span>
        <input ref="part2"
               data-testid="sort-code-part-2-input"
               type="text"
               inputmode="numeric"
               class="form-control input-lg sort-code-input__part"
               placeholder="00"
               autocomplete="no-auto-fill"
               :value="parts[1]"
               @input="onInput(1, $event)"
               @keydown="onKeydown(1, $event)"
               @paste="onPaste($event)"
               @focus="$event.target.select()" />
        <span class="sort-code-input__separator">-</span>
        <input ref="part3"
               data-testid="sort-code-part-3-input"
               type="text"
               inputmode="numeric"
               class="form-control input-lg sort-code-input__part"
               placeholder="00"
               autocomplete="no-auto-fill"
               :value="parts[2]"
               @input="onInput(2, $event)"
               @keydown="onKeydown(2, $event)"
               @paste="onPaste($event)"
               @focus="$event.target.select()" />
    </div>
</template>

<script>
export default {
    props: {
        modelValue: {
            type: String,
            default: '',
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            parts: ['', '', ''],
        };
    },

    computed: {
        inputRefs() {
            return [this.$refs.part1, this.$refs.part2, this.$refs.part3];
        },
    },

    watch: {
        modelValue: {
            handler(newValue) {
                if (newValue) {
                    const digits = newValue.replace(/\D/g, '');
                    this.parts = [
                        digits.substring(0, 2),
                        digits.substring(2, 4),
                        digits.substring(4, 6),
                    ];
                } else {
                    this.parts = ['', '', ''];
                }
            },
            immediate: true,
        },
    },

    methods: {
        emitValue() {
            const combined = this.parts.join('');
            this.$emit('update:modelValue', combined);
        },

        onInput(index, event) {
            const inputValue = event.target.value;
            // Filter to digits only
            const digitsOnly = inputValue.replace(/\D/g, '');

            // Check if this looks like a pasted sort code (more than 2 digits)
            if (digitsOnly.length > 2) {
                this.distributeSortCode(digitsOnly);
                return;
            }

            this.parts[index] = digitsOnly.substring(0, 2);
            event.target.value = this.parts[index];
            this.emitValue();

            // Move to next field if we have 2 digits
            if (this.parts[index].length === 2 && index < 2) {
                this.$nextTick(() => {
                    this.inputRefs[index + 1].focus();
                    this.inputRefs[index + 1].select();
                });
            }
        },

        onKeydown(index, event) {
            const input = event.target;
            const { selectionStart, selectionEnd } = input;
            const hasSelection = selectionStart !== selectionEnd;

            // Handle backspace
            if (event.key === 'Backspace') {
                if (selectionStart === 0 && !hasSelection && index > 0) {
                    event.preventDefault();
                    const prevInput = this.inputRefs[index - 1];
                    prevInput.focus();
                    this.$nextTick(() => {
                        prevInput.setSelectionRange(
                            prevInput.value.length,
                            prevInput.value.length
                        );
                    });
                }
                return;
            }

            // Handle delete
            if (event.key === 'Delete') {
                if (selectionStart === input.value.length && !hasSelection && index < 2) {
                    event.preventDefault();
                    const nextInput = this.inputRefs[index + 1];
                    if (nextInput.value.length > 0) {
                        this.parts[index + 1] = nextInput.value.substring(1);
                        this.emitValue();
                        nextInput.focus();
                        this.$nextTick(() => {
                            nextInput.setSelectionRange(0, 0);
                        });
                    }
                }
                return;
            }

            // Handle left arrow
            if (event.key === 'ArrowLeft') {
                if (selectionStart === 0 && !hasSelection && index > 0) {
                    event.preventDefault();
                    const prevInput = this.inputRefs[index - 1];
                    prevInput.focus();
                    this.$nextTick(() => {
                        prevInput.setSelectionRange(
                            prevInput.value.length,
                            prevInput.value.length
                        );
                    });
                }
                return;
            }

            // Handle right arrow
            if (event.key === 'ArrowRight') {
                if (selectionStart === input.value.length && !hasSelection && index < 2) {
                    event.preventDefault();
                    const nextInput = this.inputRefs[index + 1];
                    nextInput.focus();
                    this.$nextTick(() => {
                        nextInput.setSelectionRange(0, 0);
                    });
                }
                return;
            }

            // Block non-digit input (except control keys)
            if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                return;
            }

            // If typing a digit and the field is full, move to next field
            if (/\d/.test(event.key) && input.value.length === 2 && !hasSelection) {
                event.preventDefault();
                if (index < 2) {
                    // Overflow digit into the next field
                    const nextInput = this.inputRefs[index + 1];
                    this.parts[index + 1] = event.key;
                    nextInput.value = event.key;
                    this.emitValue();
                    this.$nextTick(() => {
                        nextInput.focus();
                        nextInput.setSelectionRange(1, 1);
                    });
                }
            }
        },

        /**
         * Distribute a sort code string across the three input fields.
         *
         * @param {string} digits
         */
        distributeSortCode(digits) {
            this.parts[0] = digits.substring(0, 2);
            this.parts[1] = digits.substring(2, 4);
            this.parts[2] = digits.substring(4, 6);

            this.inputRefs.forEach((input, i) => {
                input.value = this.parts[i];
            });

            this.emitValue();

            this.$nextTick(() => {
                if (digits.length >= 6) {
                    this.inputRefs[2].focus();
                    this.inputRefs[2].setSelectionRange(2, 2);
                } else if (digits.length >= 4) {
                    this.inputRefs[2].focus();
                    this.inputRefs[2].setSelectionRange(
                        this.parts[2].length,
                        this.parts[2].length
                    );
                } else if (digits.length >= 2) {
                    this.inputRefs[1].focus();
                    this.inputRefs[1].setSelectionRange(
                        this.parts[1].length,
                        this.parts[1].length
                    );
                }
            });
        },

        /**
         * Handle pasting a sort code into any field.
         *
         * @param {ClipboardEvent} event
         */
        onPaste(event) {
            const clipboardData = event.clipboardData || window.clipboardData;
            if (!clipboardData) {
                return;
            }

            const pastedText = clipboardData.getData('text') || clipboardData.getData('text/plain');
            const digits = pastedText.replace(/\D/g, '');

            if (digits.length > 2) {
                event.preventDefault();
                event.stopPropagation();
                this.distributeSortCode(digits);
            }
            // If 2 or fewer digits, let normal input handling take over
        },

        focus() {
            this.inputRefs[0].focus();
        },
    },
};
</script>

<style lang="less" scoped>
@import (less, reference) "../../../../less/variables.less";

.sort-code-input {
    display: flex;
    align-items: center;
    gap: @half-gutter-width;

    &__part {
        width: 55px;
        text-align: center;
    }

    &__separator {
        color: @text-muted;
    }
}
</style>
